import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Colours from '@shared/ui/atoms/Colours';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import {
  cancelPairing,
  clearPairingError,
  pollPairingStatus,
  selectKiosk,
  submitPairingCode,
} from '../data/kiosk.duck';
import { KeypadInput } from './KeypadInput';

const CODE_LENGTH = 8;

export function PairingScreen() {
  const dispatch = useDispatch();
  const kiosk = useSelector(selectKiosk);
  const [code, setCode] = useState('');

  const submit = (full: string) => {
    if (kiosk.pairing || kiosk.pairingStatus === 'pending') return;
    dispatch(submitPairingCode({ code: full }));
  };

  const handleChange = (next: string) => {
    if (kiosk.pairingError) dispatch(clearPairingError());
    setCode(next);
  };

  const resetToKeypad = () => {
    setCode('');
    dispatch(cancelPairing());
  };

  useEffect(() => {
    if (kiosk.pairingStatus !== 'pending' || !kiosk.pendingRequestId) return;
    const requestId = kiosk.pendingRequestId;
    const interval = window.setInterval(() => {
      dispatch(pollPairingStatus({ requestId }));
    }, Math.max(500, kiosk.pollIntervalMs));
    return () => window.clearInterval(interval);
  }, [dispatch, kiosk.pairingStatus, kiosk.pendingRequestId, kiosk.pollIntervalMs]);

  return (
    <Container>
      <BrandLogo src="/assets/images/logos/team-today-logo.svg" alt="Team Today" />

      {kiosk.pairingStatus === 'pending' ? (
        <WaitingPanel expiresAt={kiosk.pendingExpiresAt} onCancel={resetToKeypad} />
      ) : kiosk.pairingStatus === 'rejected' ? (
        <ResultPanel
          title="Connection rejected"
          message="Your admin declined the pairing request. Ask them to try again."
          onTryAgain={resetToKeypad}
        />
      ) : kiosk.pairingStatus === 'expired' ? (
        <ResultPanel
          title="Request expired"
          message="No one approved the pairing request in time. Try again."
          onTryAgain={resetToKeypad}
        />
      ) : (
        <>
          <HeadlineLarge style={{ marginBottom: 12, textAlign: 'center' }}>
            Pair this tablet
          </HeadlineLarge>
          <BodyRegular style={{ marginBottom: 24, textAlign: 'center', color: Colours.darkGrey }}>
            Enter the 8-digit code from your admin
          </BodyRegular>
          <KeypadInput
            value={code}
            maxLength={CODE_LENGTH}
            onChange={handleChange}
            onComplete={submit}
            disabled={kiosk.pairing}
          />
          {kiosk.pairingError && (
            <ErrorMessage>{kiosk.pairingError}</ErrorMessage>
          )}
          {kiosk.pairing && (
            <BodyRegular style={{ marginTop: 16, color: Colours.darkGrey }}>Submitting…</BodyRegular>
          )}
        </>
      )}
    </Container>
  );
}

function WaitingPanel({ expiresAt, onCancel }: { expiresAt: string | null; onCancel: () => void }) {
  const remaining = useCountdown(expiresAt);
  return (
    <>
      <Spinner />
      <HeadlineLarge style={{ marginBottom: 12, textAlign: 'center' }}>
        Waiting for approval…
      </HeadlineLarge>
      <BodyRegular style={{ marginBottom: 8, textAlign: 'center', color: Colours.darkGrey }}>
        Ask your admin to approve this tablet in the company settings &gt; Visitors area.
      </BodyRegular>
      {remaining !== null && (
        <BodyRegular style={{ textAlign: 'center', color: Colours.darkGrey }}>
          Expires in {formatCountdown(remaining)}
        </BodyRegular>
      )}
      <CancelLink onClick={onCancel}>Cancel</CancelLink>
    </>
  );
}

function ResultPanel({ title, message, onTryAgain }: { title: string; message: string; onTryAgain: () => void }) {
  return (
    <>
      <HeadlineLarge style={{ marginBottom: 12, textAlign: 'center' }}>{title}</HeadlineLarge>
      <BodyRegular style={{ marginBottom: 24, textAlign: 'center', color: Colours.darkGrey }}>
        {message}
      </BodyRegular>
      <TryAgainButton onClick={onTryAgain}>Try again</TryAgainButton>
    </>
  );
}

function useCountdown(expiresAt: string | null): number | null {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!expiresAt) return;
    const interval = window.setInterval(() => setTick(t => t + 1), 1000);
    return () => window.clearInterval(interval);
  }, [expiresAt]);
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  return ms > 0 ? ms : 0;
}

function formatCountdown(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

const BrandLogo = styled.img`
  height: 48px;
  width: auto;
  margin-bottom: 24px;
  align-self: center;
`;

const Container = styled.div`
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  padding: 32px;
  background: white;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ErrorMessage = styled.div`
  margin-top: 16px;
  padding: 12px 16px;
  background: white;
  border: 1px solid ${Colours.red};
  color: ${Colours.red};
  border-radius: 8px;
  font-weight: 500;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  margin-bottom: 16px;
  border-radius: 50%;
  border: 4px solid ${Colours.lightGrey};
  border-top-color: ${Colours.blue};
  animation: kiosk-spin 1s linear infinite;
  @keyframes kiosk-spin { to { transform: rotate(360deg); } }
`;

const CancelLink = styled.button`
  margin-top: 24px;
  background: none;
  border: none;
  color: ${Colours.darkGrey};
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
`;

const TryAgainButton = styled.button`
  padding: 12px 24px;
  background: ${Colours.blue};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
`;
